import { useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { FieldSize, Size, composeRedaction, formattedValidator, t, useFieldValidation, useFormattedInput } from '@bms2/shared';
import { Icon } from '../Icon';
import { FieldShell } from './FieldShell';
import { Pill } from './Pill';
import styles from './field.module.css';

/**
 * Text Input / Form Field (piece 3), the native input with the plan's shell.
 * With a `mask` it becomes the Formatted Input mode: the field inserts the
 * literals as digits are typed, ignores a non-digit keystroke, strips a paste
 * to its digits, and submits the raw digit string. A field validates on blur,
 * live once it has been marked, and never on load; the Formatted Input runs
 * the mask's digit count and piece 2's validator for its kind against the raw
 * digits. A confidential Formatted Input, given `revealTail`, shows its digits
 * only while focused and the Redacted Value shape otherwise. Spell check stays
 * the browser's: nothing here sets it off.
 */
export type TextInputType = 'text' | 'email' | 'tel' | 'url' | 'search' | 'password';

export interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  placeholder?: string;
  /** A message the page decided, such as a server's answer; it shows regardless of the field's own validation. */
  error?: string | null;
  disabled?: boolean;
  type?: TextInputType;
  autoComplete?: string;
  name?: string;
  /** Formatted Input: `X` is a digit placeholder, every other character a literal the field inserts. */
  mask?: string;
  /** The field's own validator over its value (the raw digits in Formatted Input mode); a Formatted Input has one by default. */
  validate?: (value: string) => string | null;
  /** The country whose format rules apply; US today, and every other country passes. */
  countryCode?: string;
  /** Open already marked, as a field re-rendered after a submit attempt: the message shows on load. */
  initiallyMarked?: boolean;
  /** A confidential field: away from focus it shows dots and this many trailing characters, the field's disclosure row. */
  revealTail?: number;
  /** The label leaves the screen and stays in the accessibility tree: a cell whose column header names it (piece 4). */
  labelHidden?: boolean;
  /** As wide as this many characters, where that length is exactly known; it wins over `size` (decision 0297). */
  chars?: number;
  /** Which of the widths the box takes, Column in a line table's cell: Small a code, Medium a name or an email, Large a description (decisions 0583, 0647). */
  size?: FieldSize;
  /** The most characters the box takes, where the server holds the value to a cap of its own. */
  maxLength?: number;
  /** A line of help under the box, the field shell's own; it gives way to an error (T3363). */
  hint?: string;
}

export const textInputDocumentedStates = [
  'default', 'placeholder', 'required', 'error', 'disabled', 'formattedPhone', 'formattedEin', 'formattedSsn', 'formattedRouting',
] as const;

const none = () => null;

export function TextInput(props: TextInputProps) {
  return props.mask ? <FormattedInput {...props} mask={props.mask} /> : <PlainInput {...props} />;
}

function PlainInput({ label, value, onChange, onBlur, required, placeholder, error, disabled, type = 'text', autoComplete, name, labelHidden, validate, initiallyMarked, chars, size = Size.Medium, maxLength, hint }: TextInputProps) {
  const validation = useFieldValidation(validate ?? none, { initiallyMarked: initiallyMarked ?? false, value });
  const shownError = error ?? validation.message;
  return (
    <FieldShell label={label} labelHidden={labelHidden ?? false} required={required ?? false} error={shownError} disabled={disabled ?? false} size={size} {...(hint ? { hint } : {})} {...(chars ? { chars } : {})}>
      {({ inputId, messageId }) => (
        <input
          id={inputId}
          className={`${styles.input} ${styles.withIcon}`}
          type={type}
          name={name}
          value={value}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          maxLength={maxLength}
          aria-invalid={shownError ? true : undefined}
          aria-describedby={shownError ? messageId : undefined}
          data-validation={validation.marked ? 'marked' : 'unmarked'}
          onChange={(e) => {
            onChange(e.target.value);
            validation.onChange(e.target.value);
          }}
          onBlur={() => {
            validation.onBlur(value);
            onBlur?.();
          }}
        />
      )}
    </FieldShell>
  );
}

const PASSTHROUGH_KEYS = new Set(['Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', 'Escape', 'Delete']);

function FormattedInput({
  label, value, onChange, onBlur, required, placeholder, error, disabled, name, mask, labelHidden, validate, countryCode = 'US', initiallyMarked, revealTail, chars, size = Size.Medium,
}: TextInputProps & { mask: string }) {
  const validator = validate ?? formattedValidator(mask, countryCode);
  const validation = useFieldValidation(validator, { initiallyMarked: initiallyMarked ?? false, value });
  const [focused, setFocused] = useState(false);
  const formatted = useFormattedInput({
    mask,
    value,
    onChange: (next) => {
      onChange(next);
      validation.onChange(next);
    },
  });
  const confidential = revealTail !== undefined;
  const redacted = confidential && !focused && value.length > 0;
  const shownError = error ?? validation.message;
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey || PASSTHROUGH_KEYS.has(e.key)) return;
    if (formatted.typeKey(e.key)) e.preventDefault();
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    formatted.pasteText(e.clipboardData.getData('text'));
  };
  const beside = confidential ? <Pill text={t('pill.confidential')} foreground="var(--tag-slate-text)" background="var(--tag-slate-bg)" /> : undefined;
  return (
    <FieldShell label={label} labelHidden={labelHidden ?? false} required={required ?? false} error={shownError} disabled={disabled ?? false} beside={beside} size={size} {...(chars ?? mask.length ? { chars: chars ?? mask.length } : {})}>
      {({ inputId, messageId }) => (
        <>
          <input
            id={inputId}
            className={`${styles.input} ${styles.withIcon} ${redacted ? styles.mono : ''}`}
            type="text"
            inputMode="numeric"
            name={name}
            value={redacted ? composeRedaction(value, revealTail) : formatted.display}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            data-mask={mask}
            data-raw-value={value}
            data-validation={validation.marked ? 'marked' : 'unmarked'}
            data-redacted={confidential ? (redacted ? 'true' : 'false') : undefined}
            data-reveal-tail={confidential ? revealTail : undefined}
            aria-invalid={shownError ? true : undefined}
            aria-describedby={shownError ? messageId : undefined}
            onChange={() => undefined}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              validation.onBlur(value);
              onBlur?.();
            }}
          />
          {redacted ? (
            <span className={styles.errorIcon} data-confidential-lock="true">
              <Icon meaning="confidential" size="inline" label={t('redacted.lock')} />
            </span>
          ) : null}
        </>
      )}
    </FieldShell>
  );
}
